import React, { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { Label } from 'components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from 'components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from 'components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { FileText, Quote } from 'lucide-react';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { useAuth } from 'contexts/AuthContext';
import {
  getContract, getJob, isTerminalJob, getFieldCatalog, patchFields, selectGstin,
  putContacts, activateContract, reprocessVersion,
  ContractDetail, FieldCatalog, ContactItem,
  TermsInfo,
} from './api';
import ContractDocumentsTab from './ContractDocumentsTab';
import ContractRatesTab from './ContractRatesTab';
import UploadContractModal from './UploadContractModal';

const TABS = ['Overview', 'Customer & KYC', 'Routes & Rates', 'Terms', 'Billing & Credit', 'SLAs', 'Documents', 'Review & Activate'];
const SECTION_FOR_TAB: Record<string, string> = { Terms: 'terms', 'Billing & Credit': 'billing_credit', SLAs: 'sla' };
// where each kind of missing item is filled in ("upload" items have no tab — set on the upload form)
const TAB_FOR_MISSING: Record<string, string> = {
  upload: 'Overview', customer: 'Customer & KYC', terms: 'Terms', billing_credit: 'Billing & Credit', sla: 'SLAs',
};

const ContractDetailPage: React.FC = () => {
  const { id } = useParams();
  const contractId = Number(id);
  const navigate = useNavigate();
  const perms = useAuth().userInfo?.perms ?? [];

  const [detail, setDetail] = useState<ContractDetail | null>(null);
  const { setBreadcrumbs } = useBreadcrumbs();
  const contractNumber = detail?.contract_number;
  useEffect(() => {
    setBreadcrumbs([{ label: 'Contracts', href: '/contracts' }, { label: contractNumber || 'Contract' }]);
  }, [setBreadcrumbs, contractNumber]);
  const [catalog, setCatalog] = useState<FieldCatalog | null>(null);
  const [tab, setTab] = useState(TABS[0]);
  // page to open the main contract at when a field's Source link is followed, or the
  // document to open (a rate sheet that couldn't be read)
  const [docPage, setDocPage] = useState<number | null>(null);
  const [docId, setDocId] = useState<number | null>(null);
  useEffect(() => { if (tab !== 'Documents') { setDocPage(null); setDocId(null); } }, [tab]);
  const viewSource = (page: number | null) => { setTab('Documents'); setDocPage(page); };
  const viewDocument = (documentId: number) => { setTab('Documents'); setDocId(documentId); };
  const [values, setValues] = useState<Record<string, string>>({});
  const [contacts, setContacts] = useState<ContactItem[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const d = await getContract(contractId);
    setDetail(d);
    setValues(Object.fromEntries(d.fields.map((f) => [f.field_key, f.final_value ?? ''])));
    setContacts(d.contacts);
    return d;
  }, [contractId]);

  // the first load can fail (e.g. a stale link to a deleted contract) — show it instead of crashing
  const [loadError, setLoadError] = useState<{ notFound: boolean; message: string } | null>(null);
  const initialLoad = useCallback(() => {
    setLoadError(null);
    load().catch((e: any) => setLoadError({ notFound: e?.status === 404, message: e?.message || 'Could not load the contract' }));
  }, [load]);
  useEffect(() => { initialLoad(); getFieldCatalog().then(setCatalog).catch(() => {}); }, [initialLoad]);

  // While processing, poll the lightweight job endpoint (not the full contract).
  // When the job finishes, load the full contract once to render the form.
  const jobId = detail?.lifecycle_status === 'processing' ? detail.job_id : null;
  useEffect(() => {
    if (!jobId) return;
    const t = setInterval(async () => {
      try {
        const job = await getJob(jobId);
        if (isTerminalJob(job.status)) {
          clearInterval(t);
          load();
        }
      } catch { /* keep polling */ }
    }, 2000);
    return () => clearInterval(t);
  }, [jobId, load]);

  if (loadError) {
    return (
      <div className="px-4 sm:px-6 pb-6 space-y-3">
        <h1 className="text-2xl font-semibold">{loadError.notFound ? 'Contract not found' : 'Could not load the contract'}</h1>
        <p className="text-sm text-muted-foreground">
          {loadError.notFound ? 'It may have been deleted, or the link is wrong.' : loadError.message}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/contracts')}>Back to contracts</Button>
          {!loadError.notFound && <Button onClick={initialLoad}>Retry</Button>}
        </div>
      </div>
    );
  }
  if (!detail) return <div className="px-4 sm:px-6 pb-6 text-muted-foreground">Loading…</div>;

  const versionId = detail.current_version?.id;
  // an active contract is locked — the backend refuses every edit, so don't offer any.
  // Logic follows the stored status: "expired" (past the end date) is only a label.
  const lifecycle = detail.lifecycle_status;
  const readOnly = lifecycle === 'active';
  // the terms fields can be shared with other contracts: they freeze once any of those
  // contracts is active, and wait while the contract that brought them is still processing
  const terms = detail.terms;
  const termsReadOnly = readOnly || !!terms?.locked || !!terms?.processing;
  const missing = detail.missing_for_activation;
  const missingByTab = missing.reduce<Record<string, string[]>>((acc, m) => {
    const t = TAB_FOR_MISSING[m.section] ?? 'Overview';
    (acc[t] = acc[t] || []).push(m.label);
    return acc;
  }, {});
  const jobFailed = lifecycle === 'needs_completion' && detail.job_status === 'failed';
  const fieldStatus = (key: string) => detail.fields.find((f) => f.field_key === key);
  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(null), 2500); };
  // rate revision labels for the Documents tab (rate sheets uploaded with a revision)
  const revisionOf = Object.fromEntries(detail.rate_sets.map((r) => [r.id, r.revision_number]));

  // ---- processing view ----
  if (lifecycle === 'processing') {
    return (
      <div className="px-4 sm:px-6 pb-6">
        <ContractHeading detail={detail} />
        <div className="mt-8 flex flex-col items-center justify-center gap-3 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          <p className="font-medium">Processing contract…</p>
          <p className="text-sm text-muted-foreground">
            Reading the document, extracting terms & rates, and enriching KYC. This runs in the background.
          </p>
        </div>
      </div>
    );
  }

  // ---- helpers ----
  const saveSection = async (section: string) => {
    if (!versionId) return;
    const keys = catalog?.sections.find((s) => s.section === section)?.fields.map((f) => f.key) || [];
    const updates = keys.map((k) => ({ field_key: k, final_value: values[k] || null }));
    await patchFields(versionId, updates);
    await load();
    flash('Saved');
  };

  const doReprocess = async () => {
    if (!versionId) return;
    setError(null);
    try {
      await reprocessVersion(versionId);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not queue reprocessing');
    }
  };

  const doActivate = async () => {
    if (!versionId) return;
    setError(null);
    try {
      await activateContract(contractId, versionId);
      await load();
      flash('Contract activated');
    } catch (e: any) {
      const missing = e?.data?.detail?.missing;
      setError(missing ? `Missing: ${missing.join(', ')}` : (e?.message || 'Activation failed'));
    }
  };

  return (
    <>
      <div className="px-4 sm:px-6 pb-6 space-y-4">
        <div className="flex items-start justify-between">
          <ContractHeading detail={detail} />
          <div className="flex gap-2">
            {detail.can_edit_details && <Button variant="outline" onClick={() => setEditOpen(true)}>Edit</Button>}
            <Button variant="outline" onClick={() => navigate('/contracts')}>Back</Button>
          </div>
        </div>

        {detail.can_edit_details && (
          <UploadContractModal
            open={editOpen}
            onOpenChange={setEditOpen}
            editing={detail}
            onSaved={async () => { setEditOpen(false); await load(); flash('Contract details saved'); }}
          />
        )}

        {msg && <div className="rounded bg-green-50 text-green-700 text-sm px-3 py-2">{msg}</div>}
        {error && <div className="rounded bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}
        {jobFailed && (
          <div className="rounded bg-red-50 text-red-700 text-sm px-3 py-2 flex items-center justify-between gap-3">
            <span>
              Automatic processing failed{detail.job_error ? `: ${detail.job_error}` : ''}. Nothing was
              extracted — retry, or fill in the fields manually.
            </span>
            <Button size="sm" variant="outline" onClick={doReprocess}>Retry processing</Button>
          </div>
        )}


        {!readOnly && missing.length > 0 && (
          <MissingChecklist missingByTab={missingByTab} count={missing.length} onGoTo={setTab} />
        )}

        {/* tabs — a count marks tabs that still have items blocking activation */}
        <div className="flex gap-1 border-b">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-2 text-sm border-b-2 -mb-px inline-flex items-center gap-1.5 ${tab === t ? 'border-blue-600 font-medium' : 'border-transparent text-muted-foreground'}`}
            >
              {t}
              {!readOnly && missingByTab[t] && (
                <span className="rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold px-1.5 leading-4">
                  {missingByTab[t].length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Overview — the details chosen on the upload form (changed via Edit) */}
        {tab === 'Overview' && <ContractOverview detail={detail} />}

        {/* Customer & KYC — sourced from the PAN/GST APIs during processing; read-only */}
        {tab === 'Customer & KYC' && (
          <div className="space-y-6">
            <div>
              <p className="text-sm text-muted-foreground mb-3">
                Verified from the customer's PAN during processing — not editable here.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <ReadOnlyField label="PAN" value={detail.kyc?.pan} />
                <ReadOnlyField label="Registered Name" value={detail.kyc?.registered_name} />
                <ReadOnlyField label="Registered Address" value={detail.kyc?.full_address} className="col-span-2" />
                <ReadOnlyField label="City" value={detail.kyc?.city} />
                <ReadOnlyField label="State" value={detail.kyc?.state} />
                <ReadOnlyField label="Pincode" value={detail.kyc?.pincode} />
                <ReadOnlyField label="Country" value={detail.kyc?.country} />
              </div>
            </div>

            <ContactsEditor contacts={contacts} setContacts={setContacts} readOnly={readOnly}
              onSave={async () => { if (versionId) { await putContacts(versionId, contacts); await load(); flash('Contacts saved'); } }} />

            <div>
              <h3 className="font-medium mb-2">GSTINs — select the one for billing <span className="text-xs font-normal text-muted-foreground">(optional)</span></h3>
              <div className="rounded-md border">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>GSTIN</TableHead><TableHead>Status</TableHead><TableHead>State</TableHead><TableHead>Billing</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {detail.gstins.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-4">No GSTINs — verify a PAN above.</TableCell></TableRow>
                    )}
                    {detail.gstins.map((g) => (
                      <TableRow key={g.id}>
                        <TableCell className="font-mono text-xs">{g.gstin}</TableCell>
                        <TableCell>{g.gst_status}</TableCell>
                        <TableCell>{g.gst_state}</TableCell>
                        <TableCell>
                          {g.is_selected_for_billing
                            ? <span className="text-green-700 text-xs font-medium">Selected</span>
                            : !readOnly && <Button size="sm" variant="outline" disabled={g.gst_status !== 'ACTIVE'}
                                onClick={async () => { if (versionId) { await selectGstin(versionId, g.id); await load(); flash('Billing GSTIN set'); } }}>
                                Set
                              </Button>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

          </div>
        )}

        {/* Routes & Rates */}
        {tab === 'Routes & Rates' && (
          <ContractRatesTab contractId={contractId} detail={detail} onChanged={load} flash={flash}
            onViewDocument={viewDocument}
            canUpload={perms.includes('contracts.upload')} canEdit={perms.includes('contracts.edit')} />
        )}

        {/* catalog-driven sections */}
        {SECTION_FOR_TAB[tab] && catalog && (
          <div className="space-y-4 max-w-5xl">
            {terms && <TermsBanner terms={terms} readOnly={readOnly} onOpen={(id) => navigate(`/contracts/${id}`)} />}
            {catalog.sections.filter((s) => s.section === SECTION_FOR_TAB[tab]).map((s) => (
              <div key={s.section} className="space-y-3">
                {s.fields.map((f) => {
                  const st = fieldStatus(f.key);
                  return (
                    <div key={f.key} className="grid grid-cols-3 items-start gap-3">
                      <Label className="flex items-center gap-1 pt-3">
                        {f.label}
                        {f.required_for_activation && <span className="text-red-500">*</span>}
                      </Label>
                      <div className="col-span-2 flex items-start gap-2">
                        {f.data_type === 'select' ? (
                          <Select disabled={termsReadOnly} value={values[f.key] || ''} onValueChange={(v) => setValues((p) => ({ ...p, [f.key]: v }))}>
                            <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                            <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : f.data_type === 'number' ? (
                          <Input type="number" disabled={termsReadOnly}
                            value={values[f.key] || ''} onChange={(e) => setValues((p) => ({ ...p, [f.key]: e.target.value }))} />
                        ) : (
                          // clause summaries run long: wrap and grow instead of cutting off. Read-only
                          // (not disabled) when locked, so the text stays at full contrast.
                          <Textarea autoResize rows={1} readOnly={termsReadOnly}
                            className={termsReadOnly ? 'bg-muted/40 focus-visible:ring-0 focus-visible:ring-offset-0' : undefined}
                            value={values[f.key] || ''} onChange={(e) => setValues((p) => ({ ...p, [f.key]: e.target.value }))} />
                        )}
                        {st?.status === 'auto_filled' && <span className="text-xs text-blue-600 whitespace-nowrap pt-3">auto-filled</span>}
                        {st?.source_excerpt && (
                          <SourceLink excerpt={st.source_excerpt} page={st.source_page ?? null}
                            onView={detail.documents.some((d) => d.document_type === 'main') ? viewSource : undefined} />
                        )}
                      </div>
                    </div>
                  );
                })}
                {!termsReadOnly && <Button onClick={() => saveSection(s.section)}>Save {s.label}</Button>}
              </div>
            ))}
          </div>
        )}

        {/* Documents — the stored files, for checking the summary against the source */}
        {tab === 'Documents' && <ContractDocumentsTab contractId={contractId} documents={detail.documents} openAtPage={docPage}
          openDocumentId={docId} revisionOf={revisionOf} />}

        {/* Review & Activate */}
        {tab === 'Review & Activate' && (
          <div className="space-y-4 max-w-2xl">
            <p className="text-sm text-muted-foreground">
              Activating validates all required fields. Once active, the contract can no longer be edited.
            </p>
            {lifecycle === 'active' && <div className="rounded bg-green-50 text-green-700 px-3 py-2 text-sm">This contract is active.</div>}
            {!readOnly && (missing.length > 0
              ? <MissingChecklist missingByTab={missingByTab} count={missing.length} onGoTo={setTab} />
              : <div className="rounded bg-green-50 text-green-700 px-3 py-2 text-sm">Everything required is filled in.</div>)}
            {!readOnly && <Button onClick={doActivate} disabled={missing.length > 0}>Activate contract</Button>}
          </div>
        )}
      </div>
    </>
  );
};

// ---- where an extracted value came from: the contract's own words, and a jump to the page ----
// Terms shared with other contracts of the customer: say so, since an edit here changes them too
const TermsBanner: React.FC<{ terms: TermsInfo; readOnly: boolean; onOpen: (contractId: number) => void }> = ({ terms, readOnly, onOpen }) => {
  if (terms.processing) {
    return <div className="rounded bg-amber-50 text-amber-800 text-sm px-3 py-2">
      These terms are still being extracted from the main contract document. They can be edited once that finishes.
    </div>;
  }
  if (!terms.shared_count && !(terms.locked && !readOnly)) return null;
  const hidden = terms.shared_count - terms.shared_with.length;
  return (
    <div className="rounded bg-blue-50 text-blue-900 text-sm px-3 py-2 space-y-0.5">
      {terms.shared_count > 0 && (
        <div>
          Shared terms ({terms.label}) — also used by{' '}
          {terms.shared_with.map((c, i) => (
            <React.Fragment key={c.id}>
              {i > 0 && ', '}
              <button className="underline" onClick={() => onOpen(c.id)}>
                {c.contract_number}{c.customer_division ? ` (${c.customer_division})` : ''}
              </button>
            </React.Fragment>
          ))}
          {hidden > 0 && `${terms.shared_with.length ? ' and ' : ''}${hidden} other contract${hidden === 1 ? '' : 's'}`}.
          {!terms.locked && !readOnly && ' Changes here apply to all of them.'}
        </div>
      )}
      {terms.locked && !readOnly && (
        <div>Locked: these terms are used by an active contract. For different terms, upload this contract with a new contract document.</div>
      )}
    </div>
  );
};

const SourceLink: React.FC<{ excerpt: string; page: number | null; onView?: (page: number | null) => void }> = ({ excerpt, page, onView }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        className="mt-2.5 inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
        title="Show where this came from in the contract"
      >
        <Quote className="h-3 w-3" />
        Source{page ? ` · p.${page}` : ''}
      </button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-96 space-y-3">
      <p className="text-xs font-medium text-muted-foreground">
        From the contract{page ? `, page ${page}` : ''}
      </p>
      <blockquote className="border-l-2 border-blue-300 pl-3 text-sm leading-relaxed">“{excerpt}”</blockquote>
      {onView && (
        <Button size="sm" variant="outline" onClick={() => onView(page)}>
          <FileText className="mr-1.5 h-3.5 w-3.5" />
          View in document{page ? ` (p.${page})` : ''}
        </Button>
      )}
    </PopoverContent>
  </Popover>
);

// ---- the upload-form details, read-only (the Edit button changes them) ----
const ContractOverview: React.FC<{ detail: ContractDetail }> = ({ detail }) => {
  const v = detail.current_version;
  const date = (d?: string | null) => (d ? dayjs(d).format('DD MMM YYYY') : null);
  const ous = (codes: string[]) => codes.map((c) => detail.org_unit_names[c] || c);
  const rateCard = detail.rate_sets.find((r) => r.id === detail.rate_set_id);
  const packaging = (v?.packaging_modes ?? []).map((m) => (m === 'Other' && v?.packaging_other ? `Other (${v.packaging_other})` : m));
  const rows: [string, React.ReactNode][] = [
    ['Validity', v?.start_date || v?.end_date ? `${date(v?.start_date) ?? '—'} – ${date(v?.end_date) ?? '—'}` : null],
    ['Rate type', v?.rate_type],
    ['Rates', rateCard ? (rateCard.revision_number === 0 ? 'Original rates' : `Revision R${rateCard.revision_number}`)
      + (rateCard.effective_from ? `, effective ${date(rateCard.effective_from)}` : '')
      + ` · ${rateCard.rows_count} row${rateCard.rows_count === 1 ? '' : 's'}` : null],
    ['MRC division', ous(detail.divisions).join(', ')],
    ['Booking branches', ous(detail.booking_branches).join(', ')],
    ['Billing branches', ous(detail.billing_branches).join(', ')],
    ['Material', v?.material_type],
    ['Material value per kg', v?.material_value_per_kg != null ? `₹${v.material_value_per_kg.toLocaleString('en-IN', { maximumFractionDigits: 2 })} / kg` : null],
    ['Mode of packaging', packaging.join(', ')],
    ['Customer division', detail.customer_division],
    ['Location', detail.customer_location],
    ['User access', detail.access_users.map((e) => detail.user_names[e] || e).join(', ')],
  ];
  return (
    <div className="max-w-4xl space-y-3">
      <dl className="grid grid-cols-[13rem_1fr] overflow-hidden rounded-lg border bg-card text-sm">
        {rows.map(([label, value], i) => (
          <React.Fragment key={label}>
            <dt className={`px-4 py-2.5 text-muted-foreground ${i ? 'border-t' : ''}`}>{label}</dt>
            <dd className={`px-4 py-2.5 ${i ? 'border-t' : ''}`}>{value || <span className="text-muted-foreground">—</span>}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
};

// ---- page heading: who the contract is with (the contract number is in the breadcrumb) ----
const ContractHeading: React.FC<{ detail: ContractDetail }> = ({ detail }) => {
  const about = [detail.customer_division, detail.customer_location, detail.current_version?.material_type].filter(Boolean);
  return (
    <div className="space-y-0.5">
      <h1 className="text-2xl font-semibold">{detail.customer_name || detail.customer_code}</h1>
      {about.length > 0 && <p className="text-sm">{about.join(' · ')}</p>}
    </div>
  );
};

// ---- what still blocks activation, grouped by the tab where it's filled in ----
const MissingChecklist: React.FC<{ missingByTab: Record<string, string[]>; count: number; onGoTo: (tab: string) => void }> = ({ missingByTab, count, onGoTo }) => (
  <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm space-y-2">
    <p className="font-medium text-amber-900">
      {count} item{count === 1 ? '' : 's'} left before this contract can be activated
    </p>
    <ul className="space-y-1">
      {Object.entries(missingByTab).map(([tab, labels]) => (
        <li key={tab} className="flex gap-2">
          {TABS.includes(tab)
            ? <button className="shrink-0 font-medium text-blue-700 hover:underline" onClick={() => onGoTo(tab)}>{tab}:</button>
            : <span className="shrink-0 font-medium text-amber-900" title="Chosen on the upload form">{tab}:</span>}
          <span className="text-amber-900">{labels.join(', ')}</span>
        </li>
      ))}
    </ul>
  </div>
);

// ---- read-only KYC display (values come from the PAN/GST APIs, never user input) ----
const ReadOnlyField: React.FC<{ label: string; value?: string | null; className?: string }> = ({ label, value, className }) => (
  <div className={className}>
    <Label className="text-muted-foreground">{label}</Label>
    <p className="mt-1 min-h-9 rounded-md border bg-muted/40 px-3 py-2 text-sm break-words">
      {value || <span className="text-muted-foreground">—</span>}
    </p>
  </div>
);

// ---- sub-editors ----
const ContactsEditor: React.FC<{ contacts: ContactItem[]; setContacts: (c: ContactItem[]) => void; onSave: () => void; readOnly: boolean }> = ({ contacts, setContacts, onSave, readOnly }) => {
  const upd = (i: number, patch: Partial<ContactItem>) => setContacts(contacts.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  return (
    <div className="space-y-3">
      <h3 className="font-medium">
        Contacts <span className="text-red-500">*</span>
        <span className="ml-2 text-xs font-normal text-muted-foreground">at least one, with a name and a phone or email</span>
      </h3>
      {contacts.map((c, i) => (
        <div key={i} className="grid grid-cols-4 gap-2">
          <Select disabled={readOnly} value={c.contact_type} onValueChange={(v) => upd(i, { contact_type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="business">Business</SelectItem>
              <SelectItem value="operations">Operations</SelectItem>
              <SelectItem value="finance">Finance</SelectItem>
            </SelectContent>
          </Select>
          <Input disabled={readOnly} placeholder="Name" value={c.name ?? ''} onChange={(e) => upd(i, { name: e.target.value })} />
          <Input disabled={readOnly} placeholder="Phone" value={c.phone ?? ''} onChange={(e) => upd(i, { phone: e.target.value })} />
          <Input disabled={readOnly} placeholder="Email" value={c.email ?? ''} onChange={(e) => upd(i, { email: e.target.value })} />
        </div>
      ))}
      {!readOnly && <div className="flex gap-2">
        <Button variant="outline" onClick={() => setContacts([...contacts, { contact_type: 'business' }])}>Add contact</Button>
        <Button onClick={onSave}>Save contacts</Button>
      </div>}
    </div>
  );
};

export default ContractDetailPage;
